import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { installAudioUnlock, startAlarm } from "../utils/alertSound";
import { MdRestaurant, MdBolt, MdClose } from "react-icons/md";
import { RoomRestaurantOrderDto } from "../utils/roomRestaurantOrdersApi";

interface NewRestaurantOrderPopupProps {
  order: RoomRestaurantOrderDto;
  /** Resolved client-side from order.buildingId against the account's
   * buildings — the order DTO only carries the id (see roomRestaurantOrdersApi.ts). */
  buildingName?: string | null;
  onDismiss: () => void;
  onView: () => void;
}

/** Center-screen modal for a just-arrived restaurant order — bigger and
 * harder to miss than a corner toast, since this is meant to interrupt
 * whatever the admin is doing. Dismissing just closes it (the order stays
 * "new" for the sidebar badge); "View Order" is what marks it seen. */
const NewRestaurantOrderPopup: React.FC<NewRestaurantOrderPopupProps> = ({
  order,
  buildingName,
  onDismiss,
  onView,
}) => {
  const navigate = useNavigate();

  // Rings while the popup is open, so a new order is heard as well as seen.
  useEffect(() => {
    installAudioUnlock();
    return startAlarm();
  }, []);

  const handleView = () => {
    onView();
    navigate("/admin/restaurant");
  };

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4 animate-overlay-fade-in"
      onClick={onDismiss}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl animate-modal-pop-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="relative flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-600 text-2xl">
              <MdRestaurant />
              <span className="absolute -top-1 -right-1 flex h-3.5 w-3.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex h-3.5 w-3.5 rounded-full bg-amber-500" />
              </span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                New Restaurant Order
                {order.isUrgent && (
                  <span className="ml-2 inline-flex items-center gap-0.5 rounded bg-orange-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-orange-700 align-middle">
                    <MdBolt /> Urgent
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-500">
                {order.orderNumber ?? `#${order.orderId}`}
                {order.orderDate
                  ? ` · ${new Date(order.orderDate).toLocaleString()}`
                  : ""}
              </p>
            </div>
          </div>
          <button
            onClick={onDismiss}
            aria-label="Dismiss"
            className="shrink-0 text-xl text-gray-400 hover:text-gray-600"
          >
            <MdClose />
          </button>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-gray-500">Guest</p>
              <p className="font-medium text-gray-900">
                {order.guestName ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Room Type</p>
              <p className="font-medium text-gray-900">
                {order.roomTypeName ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Building</p>
              <p className="font-medium text-gray-900">
                {buildingName ?? "—"}
              </p>
            </div>
            <div>
              <p className="text-gray-500">Room</p>
              <p className="font-medium text-gray-900">
                {order.roomNo ?? "—"}
              </p>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Items
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {(order.items ?? []).map((item) => (
                <div key={item.orderDetailId} className="text-sm text-gray-800">
                  <div className="flex items-center justify-between">
                    <span>
                      {item.itemName} × {item.quantity}
                    </span>
                    <span className="tabular-nums">
                      ₹{item.totalAmount.toFixed(2)}
                    </span>
                  </div>
                  {(item.cgstAmount > 0 ||
                    item.sgstAmount > 0 ||
                    item.igstAmount > 0) && (
                    <div className="text-xs text-gray-400 tabular-nums">
                      {item.igstAmount > 0
                        ? `IGST ₹${item.igstAmount.toFixed(2)}`
                        : `CGST ₹${item.cgstAmount.toFixed(2)} · SGST ₹${item.sgstAmount.toFixed(2)}`}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {order.specialInstructions && (
            <div className="text-sm">
              <p className="text-gray-500">Special instructions</p>
              <p className="text-gray-900">{order.specialInstructions}</p>
            </div>
          )}

          <div className="border-t border-gray-200 pt-3 space-y-1 text-sm">
            <div className="flex justify-between text-gray-600">
              <span>Subtotal</span>
              <span>₹{(order.subTotal ?? 0).toFixed(2)}</span>
            </div>
            {order.serviceChargeAmount > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>Service charge</span>
                <span>₹{order.serviceChargeAmount.toFixed(2)}</span>
              </div>
            )}
            {order.gstAmount > 0 && (
              <div className="flex justify-between text-gray-600">
                <span>GST</span>
                <span>₹{order.gstAmount.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between text-base font-semibold text-gray-900 pt-1">
              <span>Total</span>
              <span>₹{(order.totalAmount ?? 0).toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={onDismiss}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
          >
            Dismiss
          </button>
          <button
            onClick={handleView}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors"
          >
            View Order
          </button>
        </div>
      </div>
    </div>
  );
};

export default NewRestaurantOrderPopup;
